import type { Session } from '@supabase/supabase-js'
import { apiFetch } from '#core/composables/useApiFetch'
import { useDevLogger } from '#core/composables/useDevLogger'
import { useFeatureFlags } from '#core/composables/useFeatureFlags'
import { MemberStatusEnum } from '@forgd/shared/enums'
import { useStorage } from '@vueuse/core'
import { jwtDecode } from 'jwt-decode'
import { defineStore } from 'pinia'
import { withQuery } from 'ufo'
import { inferOnboardingState, OnboardingScenario } from '../utils/infer'
import { pendingRegistrationBody } from '../utils/registration'
import { isUserTokenMember, useCurrentUser } from './useCurrentUser'

const disallowedRedirects = ['/login', '/verify-email', '/magic-link', '/auth/callback', '/auth/finalize']

// We can add more reasons here
export type LogoutReason = 'expired'

// Re-export types from useCurrentUser for convenience and backwards compatibility
export type Me = SuccessResponseBody<'/users/me'>
export type LiquidityMe = SuccessResponseBody<'/liquidity/me'>
type Organizations = Me['organizations']
type Projects = Organizations[number]['projects']
export type Project = Projects[number]
export type Organization = Omit<Organizations[number], 'projects'>

export type AuthMe = Me | LiquidityMe

export const useAuth = defineStore('auth', () => {
  const nuxtApp = useNuxtApp()
  const logger = useDevLogger('auth')
  const route = useRoute()
  const supabase = useSupabaseClient()
  const currentUser = useCurrentUser()
  const { loadFlags } = useFeatureFlags()

  const currentSession = ref<Session | null>(null)
  const redirectTo = useStorage<string | null>('forgd:auth:redirect', null)

  const oauthOrigin = useStorage<string | null>('forgd:auth:oauth-origin', null)
  const isLoggedIn = computed(() => !!currentUser.isLoaded.value && !!currentSession.value)
  const wasPreviouslyLoggedIn = ref(false)
  const runtimeConfig = useRuntimeConfig()

  const onboardingPath = runtimeConfig.public.featureFlags.onboarding.path
  const liquidityFrontEndDomain = runtimeConfig.public.liquidityFrontEndDomain
  const currentPortal = runtimeConfig.public.portal

  const featureFlags = ref<string[]>([
    // default enabled feature flags here
    // otherwise they'll be synced once posthog is ready
    // or cached from first load
  ])

  const featureFlagsStatus = ref<'idle' | 'loading' | 'ready'>('idle')
  const flagsReady = computed(() => featureFlagsStatus.value === 'ready')

  let flagsPromise: Promise<void> | null = null

  // Store the current fetchCurrentUser promise to prevent concurrent calls
  let currentAuthPromise: Promise<boolean> | null = null

  // Global Supabase auth state listener
  let authStateSubscription: any = null

  /**
   * Initialize the auth state listener
   * This is the main entry point for the auth and will handle all the auth state changes
   * and will call the appropriate functions to handle the auth state changes
   *
   * onAuthStateChange callback must resolve quickly, otherwise it blocks supabase.auth.* async methods
   * until the callback is finished.
   *
   * Usually when the app is loaded, Supabase will emit an INITIAL_SESSION event. If there's a session, we will call fetchCurrentUser to fetch the user data.
   * If there's no session, we won't load the user data, and the middleware will handle the navigation to the login page.
   */
  function initializeAuthStateListener() {
    if (authStateSubscription) {
      return
    }
    logger.debug('[initializeAuthStateListener] initializing auth state listener')

    authStateSubscription = supabase.auth.onAuthStateChange(async (event, session) => {
      logger.debug('[onAuthStateChange] auth state change', event, 'session:', session, 'isUserLoaded:', currentUser.isLoaded.value, 'current route:', route.path)

      if (session?.access_token) {
        const decodedJwt = jwtDecode(session.access_token)
        // If market maker is accessing forgd.com, redirect to liquidity portal
        if (currentPortal === 'app' && ('marketMakerId' in decodedJwt && !!decodedJwt.marketMakerId)) {
          // Prevent race condition with waitForAuth from trying to fetch user data by setting currentAuthPromise
          currentAuthPromise = Promise.resolve(false)
          await clearLocalSession()
          const redirectUrl = `${liquidityFrontEndDomain}/magic-link#access_token=${session.access_token}&refresh_token=${session.refresh_token}`
          logger.debug(`[onAuthStateChange] market maker detected on app portal, redirecting to liquidity portal`, redirectUrl)
          await navigateTo(redirectUrl, { external: true })
          return
        }
      }

      currentSession.value = session
      if (event === 'INITIAL_SESSION' && !!session && !currentUser.isLoaded.value) {
        logger.debug('[onAuthStateChange] initial session valid but user data is not loaded, will call fetchCurrentUser')
        await fetchCurrentUser(session)
        wasPreviouslyLoggedIn.value = true
      }
      else if (event === 'INITIAL_SESSION' && !session && !currentUser.isLoaded.value) {
        logger.debug('[onAuthStateChange] no session found, user not authenticated')
        // Don't navigate here - let the middleware handle navigation
        // The middleware will check auth.isLoggedIn and redirect appropriately
      }
      else if (event === 'SIGNED_OUT') {
        logger.debug('[onAuthStateChange] signed out, clearing auth state')
        await clear()
      }
      else if (event === 'SIGNED_IN' && session && !currentUser.isLoaded.value) {
        logger.debug('[onAuthStateChange] user signed in and user data is not loaded, will call fetchCurrentUser')
        // Only refresh auth state if user is not already logged in
        await fetchCurrentUser(session)
        wasPreviouslyLoggedIn.value = true
      }
      else if (event === 'SIGNED_IN' && session && currentUser.isLoaded.value) {
        logger.debug('[onAuthStateChange] user signed in and already logged in, doing nothing')
        wasPreviouslyLoggedIn.value = true
      }

      logger.debug('[onAuthStateChange] auth state change done for event', event, '✅', 'wasPreviouslyLoggedIn', wasPreviouslyLoggedIn.value)
    })
  }

  // Initialize the listener when the store is created
  if (import.meta.client) {
    initializeAuthStateListener()
  }

  /**
   * Consumes registration details parked in Supabase user metadata by the
   * email+password signup modal. The confirmation email can land the user on
   * any page (/auth/finalize, legacy /confirm-email, or the site_url fallback),
   * so the project must be created on whichever authenticated boot happens
   * first — otherwise the onboarding guard captures the user into the legacy
   * /onboarding flow.
   */
  async function maybeFinalizeRegistration(session: Session) {
    const me = currentUser.me.value
    if (currentPortal !== 'app' || !isUserTokenMember(me) || me.isOnboarded) {
      return
    }
    const body = pendingRegistrationBody(session.user)
    if (!body) {
      return
    }
    const ownOrgWithoutProject = me.organizations.find(
      org => org.memberStatus === MemberStatusEnum.Active && org.ownerUserId === me.id && !org.projects.length,
    )
    if (!ownOrgWithoutProject) {
      return
    }
    try {
      logger.debug('[maybeFinalizeRegistration] finalizing pending registration')
      await nuxtApp.runWithContext(() => apiFetch('/onboarding/finalize-registration', { method: 'post', params: { body } }))
      await currentUser.fetch(session)
    }
    catch (error) {
      // Leave the user to the onboarding guard rather than block auth.
      logger.error('[maybeFinalizeRegistration] failed to finalize registration', error)
    }
  }

  /**
   * Creates a promise to fetch the current user. The promise is stored in the currentAuthPromise variable
   * and is used to prevent concurrent calls to fetchCurrentUser
   *
   * @param session - The session to fetch the current user for
   * @param isRefresh - Whether we are simply refreshing the state
   * @returns A promise that resolves to true if the user is authenticated, false otherwise
   */
  async function fetchCurrentUser(session: Session | null, isRefresh = false) {
    if (currentAuthPromise) {
      logger.debug('[fetchCurrentUser] authentication already in progress, returning existing promise')
      return currentAuthPromise
    }

    logger.debug('[fetchCurrentUser] session', session, 'isRefresh', isRefresh)
    // Create and store the authentication promise with the entire try-catch-finally logic
    currentAuthPromise = (async () => {
      try {
        if (!session) {
          throw createError({ statusCode: 401, statusMessage: 'Unauthorized' })
        }
        // Identify user for analytics tracking
        useTracer().identify(session.user.id, { email: session.user.email })
        const [isLoaded] = await Promise.all([
          currentUser.fetch(session),
          // Feature flags are allowed to fail without breaking auth; ensureFlagsLoaded
          // swallows errors internally and sets status to 'ready' regardless. Force a
          // reload because flags are per-user and any anonymous set is now stale.
          ensureFlagsLoaded({ force: true }),
        ])
        logger.debug('[fetchCurrentUser] feature flags loaded', featureFlags.value)
        await maybeFinalizeRegistration(session)
        // if we're just refreshing auth we don't need to call this hook again
        if (!isRefresh) {
          // allow logic to run as we sign in outside of useAuth
          await nuxtApp.hooks.callHook('forgd:auth:authenticated', { me: currentUser.me.value })
        }
        logger.debug('[fetchCurrentUser] fetchCurrentUser completed', isLoaded)
        return isLoaded
      }
      catch (e: any) {
        logger.error('[fetchCurrentUser] error', e)
        if (Number(e.statusCode) !== 200) {
          let reason: LogoutReason | undefined
          if (Number(e.statusCode) === 401) {
            reason = 'expired'
          }
          logger.debug('auth fetch failed, redirecting to login', { statusCode: e.statusCode, path: route.path, reason })
          await navigateToLogin({ from: 'fetchCurrentUser' }, reason)
        }
        logger.debug('[fetchCurrentUser] user data not loaded, returning false')
        return false
      }
      finally {
        logger.debug('[fetchCurrentUser] clearing currentAuthPromise')
        currentAuthPromise = null
      }
    })()

    return currentAuthPromise
  }

  /**
   * Forces a refresh of the auth state
   *
   * @param options - The options for the refresh
   * @param options.from - The path from which the refresh is being made
   * @returns A promise that resolves when the auth state is refreshed
   */
  async function refresh(options: { from?: string }) {
    logger.debug(`[refresh=${options.from}] will refresh auth state`)
    const { data: { session } } = await supabase.auth.getSession()
    await fetchCurrentUser(session, true)
  }

  /**
   * Waits for the auth state to be loaded. We should only use this in auth plugin and middleware.
   * Other middlewares should use isLoggedIn instead.
   */
  async function waitForAuth(options?: { from?: string }) {
    if (currentAuthPromise) {
      logger.debug(`[waitForAuth=${options?.from}] auth already in progress, waiting for existing promise`)
      return currentAuthPromise
    }

    const { data: { session } } = await supabase.auth.getSession()
    // If we had a session before but now it's null, the session was cleared. The middleware will handle
    // clearing user data and navigating to the login page.
    if (!session && currentSession.value) {
      logger.debug(`[waitForAuth=${options?.from}] session was cleared, updating auth state`)
      currentSession.value = null
      return false
    }

    if (session && !currentUser.isLoaded.value) {
      logger.debug(`[waitForAuth=${options?.from}] session exists but user not loaded, fetching user data`)
      return fetchCurrentUser(session, true)
    }

    logger.debug(`[waitForAuth=${options?.from}] auth state loaded, returning isLoggedIn`, isLoggedIn.value)
    return isLoggedIn.value
  }

  /**
   * Clears the auth state and triggers the reset hook
   * Note: this needs to be called in addition to the Supabase signOut function
   *
   * @returns A promise that resolves when the auth state is cleared
   */
  function setFeatureFlags(flags: string[]) {
    featureFlags.value = flags
    featureFlagsStatus.value = 'ready'
  }

  /**
   * Loads feature flags (deduplicated). Safe to call from multiple boot paths (auth
   * flow, plugin) — concurrent callers share one in-flight request. Resolves to
   * 'ready' even on failure so the app never blocks forever on flags.
   *
   * Pass `{ force: true }` when the identity changes (login/refresh): flags are
   * evaluated per-user, so an already-loaded anonymous set must be re-fetched for
   * the authenticated user.
   */
  async function ensureFlagsLoaded(options?: { force?: boolean }): Promise<void> {
    const force = options?.force ?? false
    if (flagsReady.value && !force) {
      return
    }
    // If a load is already in flight, wait for it. A non-forced caller is then done;
    // a forced caller falls through to trigger a fresh load for the new identity.
    if (flagsPromise) {
      await flagsPromise
      if (!force) {
        return
      }
    }
    featureFlagsStatus.value = 'loading'
    flagsPromise = (async () => {
      const loaded = await loadFlags().catch(() => [])
      featureFlags.value = loaded
      featureFlagsStatus.value = 'ready'
    })().finally(() => {
      flagsPromise = null
    })
    return flagsPromise
  }

  async function clear() {
    currentUser.clear()
    currentSession.value = null
    redirectTo.value = null

    // Re-fetch flags as anonymous
    void ensureFlagsLoaded({ force: true })

    logger.debug('[clear] cleared auth state')
    await nuxtApp.hooks.callHook('forgd:auth:reset')
  }

  function currentRouteRequiresAuth() {
    if (route.meta.allowPreLogin) {
      return false
    }
    const middleware = route.meta.middleware
    const middlewares = Array.isArray(middleware) ? middleware : (middleware ? [middleware] : [])
    return middlewares.includes('auth')
  }

  /**
   * Signs out the user. This will trigger the onAuthStateChange listener, redirecting to the login page
   * and clearing the auth state
   *
   * @returns A promise that resolves when the user is signed out
   */
  async function logout(reason?: LogoutReason, redirectToLogin = true) {
    logger.debug('[logout] will sign out')
    const isAppPortal = currentPortal === 'app'
    // This will trigger the onAuthStateChange listener, clearing the auth state
    await supabase.auth.signOut()

    if (redirectToLogin) {
      // On the main app, logging out keeps the user on the public site instead of
      // bouncing them to the login page. Public bare pages stay put and re-render
      // as anonymous. Project-scoped pages leave via a full reload — a clean
      // anonymous boot that also keeps the project-id middleware from re-scoping
      // the target: dual-mode pages land on their public twin, strictly protected
      // pages on the public homepage.
      if (isAppPortal) {
        if (route.path.startsWith('/project/')) {
          const publicTwinPath = route.meta.allowPreLogin ? route.path.replace(/^\/project\/[^/]+/, '') : ''
          await navigateTo(publicTwinPath || '/', { external: true })
        }
        else if (currentRouteRequiresAuth()) {
          await navigateTo('/', { external: true })
        }
      }
      else {
        await navigateToLogin({ from: 'logout' }, reason)
      }
    }
    wasPreviouslyLoggedIn.value = false
  }

  // Manually clear Supabase storage keys without calling signOut to avoid any API calls
  async function clearLocalSession() {
    if (!import.meta.client) {
      return
    }
    // Clear local state
    currentSession.value = null
    await clear()

    const cookiePrefix = runtimeConfig.public.supabase.cookiePrefix || 'sb'
    try {
      // Clear all Supabase auth keys from storage
      const storageKey = `${cookiePrefix}-${new URL(runtimeConfig.public.supabase.url).hostname.split('.')[0]}-auth-token`
      localStorage.removeItem(storageKey)
      // Also clear from cookies if they exist
      const expiry = 'Thu, 01 Jan 1970 00:00:00 GMT'
      const hostname = window.location.hostname
      const domainParts = hostname.split('.')
      const apexDomain = domainParts.length > 1 ? domainParts.slice(-2).join('.') : hostname

      document.cookie.split(';').forEach((c) => {
        const cookieName = c.trim().split('=')[0]
        if (cookieName?.startsWith(cookiePrefix)) {
          document.cookie = `${cookieName}=;expires=${expiry};path=/`
          document.cookie = `${cookieName}=;expires=${expiry};path=/;domain=.${apexDomain}`
        }
      })
      logger.debug(`[clearLocalSession] manually cleared Supabase storage`)
    }
    catch (error) {
      logger.debug(`[clearLocalSession] error clearing storage (ignored):`, error)
    }
  };

  /**
   * Navigates to the home page, depending on the user's role
   *
   * @param options - The options for the navigation
   * @param options.from - The path from which the navigation is being made
   */
  async function navigateToHome(options: { from?: string }) {
    let redirectToUrl = currentPortal === 'app' ? '/' : '/dashboard'
    logger.debug(`[navigateToHome=${options.from}] checking where to redirect`, {
      redirectToUrl,
      currentUser: currentUser.me.value,
      isMarketMaker: currentUser.isMarketMaker.value,
      isAdvisor: currentUser.isAdvisor.value,
      organizations: currentUser.organizations.value,
    })

    if (!currentUser.isLoaded.value) {
      await navigateToLogin({ from: 'navigateToHome' })
      return
    }
    // liquidity.forgd.com
    else if (currentUser.me.value && currentUser.isMarketMaker.value) {
      redirectToUrl = redirectTo.value || '/dashboard'
      redirectTo.value = null
    }
    // advisor portal
    else if (currentUser.isAdvisor.value) {
      redirectToUrl = '/advisor-portal'
      redirectTo.value = null
    }
    // forgd.com
    else if (currentUser.organizations.value?.length && isUserTokenMember(currentUser.me.value)) {
      const state = inferOnboardingState(currentUser.me.value)

      if (state.scenario === OnboardingScenario.OWNER_ACTIVE_WITH_PROJECTS) {
        redirectToUrl = redirectTo.value || state.redirectTo
        redirectTo.value = null
      }
      else {
        redirectToUrl = state.redirectTo
        redirectTo.value = null
      }
    }

    logger.debug(`[navigateToHome=${options.from}] redirecting to`, redirectToUrl)
    await navigateTo(redirectToUrl)
  }

  function setRedirectTo(path: string) {
    const target = stripAuthCallbackHash(path)
    if (disallowedRedirects.includes(target)) {
      logger.debug('[setRedirectTo] disallowed post-auth redirect', target)
      return
    }
    logger.debug('[setRedirectTo] setting post-auth redirect', target)
    redirectTo.value = target
  }

  function consumeRedirectTo() {
    const path = redirectTo.value
    redirectTo.value = null
    logger.debug('[consumeRedirectTo] consuming post-auth redirect', path)
    return path
  }

  function clearRedirectTo() {
    redirectTo.value = null
  }

  function consumeOAuthOrigin() {
    const origin = oauthOrigin.value
    oauthOrigin.value = null
    logger.debug('[consumeOAuthOrigin] consuming oauth origin', origin)
    return origin
  }

  async function updateSession(from: string, accessToken: string, refreshToken: string) {
    logger.debug(`[updateSession=${from}] will update session`, { accessToken, refreshToken })

    const { data, error } = await supabase.auth.setSession({
      access_token: accessToken,
      refresh_token: refreshToken,
    })
    logger.debug(`[updateSession=${from}] after setSession`, { data, error })

    if (error) {
      logger.error(`[updateSession=${from}] error`, error)
      await navigateToLogin({ from: 'updateSession' })
      return null
    }

    if (!data.session) {
      logger.error(`[updateSession=${from}] no session data found in auth response`)
      // todo: show error message
      return null
    }

    logger.debug(`[updateSession=${from}] session set successfully, will redirect`)
    await navigateToHome({ from })

    return { data, error }
  }

  async function loginWithGoogle(options?: { redirectPath?: string }) {
    if (import.meta.server) {
      logger.debug('[loginWithGoogle] skipping OAuth initiation during SSR')
      return
    }

    const origin = options?.redirectPath || (window.location.pathname + window.location.search)
    const isAllowedOrigin = !!origin && !disallowedRedirects.some(
      d => origin === d || origin.startsWith(`${d}/`) || origin.startsWith(`${d}?`),
    )
    oauthOrigin.value = isAllowedOrigin ? origin : null
    if (isAllowedOrigin) {
      setRedirectTo(origin)
    }

    const redirectTo = new URL('/auth/callback', window.location.origin).toString()
    logger.debug('[loginWithGoogle] initiating Google OAuth', { redirectTo, origin })

    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo,
        queryParams: {
          prompt: 'select_account',
        },
      },
    })

    if (error) {
      logger.error('[loginWithGoogle] error initiating Google OAuth', error)
      throw error
    }

    return data
  }

  function setSelectedProject(val: Project | null) {
    if (val) {
      logger.debug('[setSelectedProject]', { id: val.id, name: val.name })
      currentUser.setSelectedProject(val)
    }
    else {
      logger.debug('[setSelectedProject] clearing project')
      currentUser.clearSelectedProject()
    }
  }

  async function navigateToLogin(options?: { from?: string }, reason?: LogoutReason) {
    // Skip navigation during SSR - let middleware handle it
    if (import.meta.server) {
      logger.debug(`[navigateToLogin=${options?.from}] skipping navigation during SSR`)
      return
    }
    const query: Record<string, string> = {}
    if (reason) {
      query.reason = reason
    }
    const loginUrl = withQuery('/login', query)
    logger.debug(`[navigateToLogin=${options?.from}] about to navigate to`, loginUrl, 'current route:', route.path)

    return navigateTo(loginUrl)
  }

  function log(message: string, ...args: any[]) {
    logger.log(message, ...args)
  }

  function debug(message: string, ...args: any[]) {
    logger.debug(message, ...args)
  }

  return {
    featureFlags,
    featureFlagsStatus,
    flagsReady,
    isLoggedIn,
    wasPreviouslyLoggedIn,
    onboardingPath,
    // current user data; kept for backwards compatibility
    me: currentUser.me,
    liquidityMe: currentUser.liquidityMe,
    organization: currentUser.organization,
    organizations: currentUser.organizations,
    isAdvisor: currentUser.isAdvisor,
    isOrganizationOwner: currentUser.isOrganizationOwner,
    project: currentUser.project,
    ticker: currentUser.ticker,
    isMarketMaker: currentUser.isMarketMaker,
    isTokenMember: currentUser.isTokenMember,
    selectedProjectId: currentUser.selectedProjectId,
    findProjectAndOrg: currentUser.findProjectAndOrg,
    // methods
    setRedirectTo,
    consumeRedirectTo,
    clearRedirectTo,
    consumeOAuthOrigin,
    setFeatureFlags,
    ensureFlagsLoaded,
    clear,
    logout,
    refresh,
    navigateToHome,
    navigateToLogin,
    updateSession,
    loginWithGoogle,
    setSelectedProject,
    waitForAuth,
    log,
    debug,
  }
})
